import { applyFeederBudgetToCost } from 'lib/checkout/flights/cart'
import { getBudgetedSelectedDepartingLegPrice } from 'checkout/selectors/view/flights'
import moment from 'moment'
import { buildBookingTitleGenderMap } from 'checkout/lib/utils/cruises/booking'
import { BNBL_VERSION_UPFRONT_SURCHARGE, RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { createSelector } from 'reselect'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { deduceGenderFromTitle, getPackageOptionType } from 'lib/tours/tourUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import {
  isAccommodationItem,
  isAccommodationV2Item,
  findPostPurchaseCheckout,
  getConnectRoomsSpecialRequestMessage,
  isBedbankItem,
  isBNBLLEHotelItem,
  isCarHireItem,
  isCruiseItem,
  isFlightItem,
  isInstantBookingLEHotelItem,
  isTourV1Item,
  isTourV2Item,
  isPurchaseCreditItem,
} from 'lib/checkout/checkoutUtils'
import { arrayToMap, EmptyArray, groupBy, sum } from 'lib/array/arrayUtils'
import {
  CHECKOUT_ITEM_TYPE_FLIGHT,
  GIFT_RECIPIENT_FORM_ID,
  PRIMARY_TRAVELLER_FORM_ID,
} from 'constants/checkout'
import { ITEM_TYPE_ACCOMMODATION_V2, ITEM_TYPE_BEDBANK, ITEM_TYPE_EXPERIENCE, ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_BOOKING_PROTECTION, ITEM_TYPE_PURCHASE_CREDIT } from 'constants/cart'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'
import { computeBaggageCostForPaxList } from 'checkout/lib/utils/flights/extras'
import { getSelectedTransferExtras, getTransferExtrasTotal } from 'checkout/lib/utils/experiences/transferExtras'
import {
  OFFER_DYNAMIC_PRICING_TYPES,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import { buildBaggagePayload, buildPassengerPayload } from 'checkout/lib/utils/flights/order'
import { getDateChangeJourneyV2IdKey, getEffectiveFlightPrice, getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import type { Order } from '@luxuryescapes/contract-svc-order'
import { generateScheduleDate } from 'lib/payment/gift'
import { getInstantBookingPriceAndSurcharge } from 'checkout/lib/utils/accommodation/price'
import getTransferItems from 'checkout/selectors/view/items/getTransferItems'
import getInsuranceItemsView from 'checkout/selectors/view/itemViews/getInsuranceItemsView'
import { isCheckoutPostPurchaseInsurance } from 'checkout/selectors/view/insurance'
import getCheckoutSelectedQuotes from 'checkout/selectors/view/getCheckoutSelectedQuote'
import {
  validateInsuranceTravellerCount,
  isInsuranceTravellerOverSubmission,
  logInsuranceTravellerOverSubmission,
  logInsuranceTravellerMismatch,
  logInsuranceTravellerCompositionMismatch,
} from 'checkout/lib/utils/insurance/validateInsuranceTravellerCount'
import { alignTravellersToQuotedComposition } from 'checkout/lib/utils/insurance/alignTravellersToQuotedComposition'
import { getAge } from 'lib/datetime/dateUtils'
import { getCheckoutCruiseOfferView, isCruiseCheckout } from 'checkout/selectors/view/cruise'
import { getBookingSessionData } from 'checkout/selectors/cruiseSelectors'
import { TRANSPORTATION_CODE } from 'components/CarHire/CarHireHardcodedLists'
import {
  getAccommodationItems,
  getLeInstantBookingItems,
  getTourV2CheckoutItems,
} from 'checkout/selectors/view/accommodation'
import { getBundleAndSaveItemViews } from 'checkout/selectors/view/bundleAndSave'
import { getGiftCardItems } from 'checkout/selectors/view/giftCard'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import getCheckoutQuoteSessionId from 'checkout/selectors/request/getCheckoutQuoteSessionId'
import { baggageOptionsMap } from 'api/lib/convertJourneyV2'
import { getVillaViews } from '../view/villa'
import { getBookingProtectionItems } from '../view/bookingProtection'
import { getLuxPlusSubscriptionItems, checkoutWithMemberPrice, getSubscriptionJoinItems, getShouldUseCruiseMemberPrice } from '../view/luxPlusSubscription'
import { getFullOffers } from 'selectors/offerSelectors'
import getBookingProtectionDetails from 'checkout/selectors/request/getBookingProtectionDetails'
import { getCheckoutInsuranceTravellers } from 'checkout/selectors/request/getCheckoutInsuranceTravellers'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getBedbankChangeDatesTravellers } from '../bedbankChangeDatesTravellers'
import { getTourV2ExperienceItems } from '../view/toursv2'
import { getIsPostPurchaseTourChangeDates, getIsPostPurchaseTourOptionalExperience } from 'checkout/selectors/cartModeSelectors'
import { getCarHireSelectedRateOption } from 'checkout/lib/utils/carHire/view'
import { getOrderSubscriber } from 'luxPlus/selectors/checkout/order'
import { FlightViewTypes } from 'constants/flight'
import { floatify } from 'lib/maths/mathUtils'
import { getLereDynamicPricingGroup, getLereDynamicPricingGroupForOffer } from 'checkout/lib/utils/lereDynamicPricing'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import {
  resolveChannelMarkupDetails,
  resolveChannelMarkupOrderIdentity,
  resolveMemberChannelMarkupDetails,
} from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import type { PromoItemDiscountsWithStatus } from 'lib/promo/promoMappers'
import { filterItemDiscounts } from 'lib/promo/filterItemDiscounts'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'
import getIsLuxPlusPricedFlight from './getLuxPlusPricedFlight'
import { getBedbankItemIdToLeadItemId } from './accommodation'
import { getAccommodationAndExperienceItemDiscounts, getAllPromoCodeItemDiscounts, getCruiseItemDiscounts, getGiftCardItemDiscounts, getInsuranceItemDiscounts, getSubscriptionJoinDiscountItems, getTransferItemDiscounts } from './checkout'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getExperienceItemSourceApp from './getExperienceItemSourceApp'
import getFormattedLoyaltyLoungePassItems from './orderCreation/getFormattedLoyaltyLoungePassItems'
import { getFormattedExperienceItems } from './orderCreation/getFormattedExperienceItems'
import { getFormattedTravellers } from './orderCreation/getFormattedTravellers'
import formatItemDiscount from 'lib/payment/formatDiscountItem'
import {
  getFormattedLuxPlusSubscriptionItems,
  type FormattedOrderSubscriptionItem,
} from './luxPlus/getFormattedLuxPlusSubscriptionItems'
import type { ExperienceOrderCreationItem, LoyaltyPurchasePointsOrderCreationItem, MerchandiseOrderCreationItem, MerchandiseShippingOrderCreationItem } from 'api/order'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import getFormattedMerchandiseShippingItems from './orderCreation/getFormattedMerchandiseShippingItems'
import getFormattedMerchandiseItems from './orderCreation/getFormattedMerchandiseItems'
import getFormattedLoyaltyPurchasePointsItems from './orderCreation/getFormattedLoyaltyPurchasePointsItems'
import { getCartHasPropertyCollectItem, isAccommodationV2PropertyCollectRecord } from './propertyCollectSelectors'
import { isPostPurchaseAncillaryPayment } from './isPostPurchaseAncillaryPayment'
import getShouldEarnLoyaltyPoints from 'checkout/selectors/loyalty/getShouldEarnLoyaltyPoints'
import { getCheckoutRoomGuestItems, getMappedRoomGuestEligibleItems, getPrimaryRoomGuestItemId, getResolvedRoomGuestNamesByItemId, getRoomGuestItems, getRoomGuestNames, getRoomGuestPropertyIdByItemId, isRoomGuestPropertyMappingPending, isSameRoomGuestName, shouldDefaultFirstCollectedRoomToPrimary } from 'checkout/lib/utils/roomGuestFields'
import isOverlappingRoomGuestNamesEnabled, { isOverlappingFlashRoomGuestNamesEnabled } from 'checkout/selectors/request/isOverlappingRoomGuestNamesEnabled'
import getEffectiveLeHotelPackage from 'checkout/lib/utils/accommodation/getEffectiveLeHotelPackage'
import type { RoomGuestItem } from 'checkout/lib/utils/roomGuestFields'

function parseStreetNumber(street: string): { streetNumber: string; streetName: string } {
  const match = street.match(/^(\d+[a-zA-Z]?)\s+(.+)$/)
  return match ? { streetNumber: match[1], streetName: match[2] } : { streetNumber: '', streetName: street }
}

export function getPassportCardFallbackTravellerForms(
  travellerForms: Array<App.Checkout.TravellerForm>,
  insuranceTravellers: App.Occupants,
): Array<App.Checkout.TravellerForm> {
  // PassportCard Accept must match the traveller count used for the quote.
  // If the item has no persisted PassportCard travellers, only fall back to the
  // checkout traveller forms that correspond to the quoted insurance occupancy.
  const adultCount = insuranceTravellers.adults ?? 0
  const childCount = (insuranceTravellers.children ?? 0) + (insuranceTravellers.infants ?? 0)
  const totalCount = adultCount + childCount

  const adultForms = travellerForms.filter(traveller => traveller.id.startsWith('adult-'))
  const childForms = travellerForms.filter(traveller => !traveller.id.startsWith('adult-'))
  const selectedForms = [
    ...adultForms.slice(0, adultCount),
    ...childForms.slice(0, childCount),
  ]

  if (selectedForms.length >= totalCount) return selectedForms

  const selectedFormIds = new Set(selectedForms.map(traveller => traveller.id))
  const remainingForms = travellerForms.filter(traveller => !selectedFormIds.has(traveller.id))

  return [
    ...selectedForms,
    ...remainingForms.slice(0, totalCount - selectedForms.length),
  ]
}

function getContactName(primaryContact: App.Checkout.TravellerForm | undefined, account: App.AuthAccount) {
  if (primaryContact) {
    return `${primaryContact.data.firstName} ${primaryContact.data.lastName}`
  } else {
    let contactName = account.givenName
    if (account.surname) contactName += ` ${account.surname}`
    return contactName
  }
}

function addConnectRoomsSpecialRequestMessage(specialRequest: string | undefined, contactNames: string | Array<string>) {
  const hasExistingSpecialRequest = !!specialRequest?.replace(/,/g, '').trim()
  const prefix = hasExistingSpecialRequest ? `${specialRequest} ` : ''
  return `${prefix}${getConnectRoomsSpecialRequestMessage(contactNames, getCurrentIntl())}`
}

interface InsuranceOrderTravellerDetail {
  firstname: string | undefined
  surname: string | undefined
  age_range: string
}

interface InsuranceOrderTravellerDetailsResult {
  travellersDetails: Array<InsuranceOrderTravellerDetail>
  isUsingCustomTravellers: boolean
}

export function shouldUseInsuranceItemCustomTravellers(
  item: App.Checkout.InsuranceItem,
  isCruiseCheckout: boolean,
  isPostPurchaseInsurance: boolean,
): boolean {
  if (item.insuranceType === 'cfmr') return false
  // PassportCard collects its travellers in the insurance section itself, so an item carrying
  // them needs no vertical traveller forms - a post-purchase insurance-only cart has none.
  if (item.productId === PRODUCT_ID_PASSPORT_CARD && item.passportCardTravellers?.length) return true

  const isCruisePostPurchase = isCruiseCheckout && isPostPurchaseInsurance
  return !!item.travellers?.length && (!isCruiseCheckout || isCruisePostPurchase)
}

export function getCheckoutInsuranceOrderTravellerDetails(
  item: App.Checkout.InsuranceItem,
  travellers: Array<App.Checkout.TravellerForm>,
  isCruiseCheckout: boolean,
  isPostPurchaseInsurance: boolean,
  quotedTravellers: App.InsuranceQuote['requestedTravellers'],
): InsuranceOrderTravellerDetailsResult {
  const isUsingCustomTravellers = shouldUseInsuranceItemCustomTravellers(
    item,
    isCruiseCheckout,
    isPostPurchaseInsurance,
  )

  if (isUsingCustomTravellers) {
    const customTravellers = item.travellers?.length ? item.travellers : (item.passportCardTravellers ?? [])
    return {
      isUsingCustomTravellers,
      travellersDetails: customTravellers.map(traveller => ({
        firstname: traveller.firstName,
        surname: traveller.lastName,
        age_range: traveller.ageRange,
      })),
    }
  }

  // The submitted age-group composition must equal the composition the selected
  // quote was priced for; the traveller forms only supply who fills those slots.
  return {
    isUsingCustomTravellers,
    travellersDetails: alignTravellersToQuotedComposition(travellers, quotedTravellers),
  }
}

function getRoomGuestContactNames({
  roomGuestItems,
  primaryForm,
  travellerForms,
  formSchema,
  fallbackFirstName,
  fallbackLastName,
  defaultFirstRoomToPrimary = true,
}: {
  roomGuestItems: Array<RoomGuestItem>
  primaryForm: App.Checkout.TravellerForm | undefined
  travellerForms: Array<App.Checkout.TravellerForm>
  formSchema: App.Checkout.FormSchema | undefined
  fallbackFirstName: string | undefined
  fallbackLastName: string | undefined
  defaultFirstRoomToPrimary?: boolean
}) {
  const contactNames = roomGuestItems.map((roomGuestItem, index) => {
    const guestNames = getRoomGuestNames({
      itemId: roomGuestItem.itemId,
      primaryForm,
      travellerForms,
      formSchema,
      fallbackFirstName,
      fallbackLastName,
      isEnabled: true,
      isPrimaryRoom: defaultFirstRoomToPrimary && index === 0,
    })

    return [guestNames.firstName, guestNames.lastName].filter(Boolean).join(' ').trim()
  }).filter(Boolean)

  return [...new Set(contactNames)]
}

export { getFormattedTravellers }

export const getFormattedTransferItems = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getTransferItemDiscounts(state),
  (state: App.State) => getExperienceItemSourceApp(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getIsEnabledLuxPlusDiscountedExperiences(state),
  (state: App.State) => getFormattedTravellers(state),
  (state: App.State) => getBedbankItemIdToLeadItemId(state),
  (transferItems, experiences, transferItemDiscounts, sourceApp, checkoutWithMemberPrice, isLuxPlusDiscountedExperiencesEnabled, formattedTravellers, bedbankItemIdToLeadItemId) => {
    return transferItems.map((item) => {
      const experience = experiences[item.experienceId]
      const { transfer, transactionKey } = item

      if (experience && transfer.option && transferItemDiscounts.hasRequiredPromoCodeData) {
        const [hour, minute] = transfer.time?.split(':') ?? ['00', '00']
        const fullDateTime = moment.utc(transfer.date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')
        const dealOptionsTime = transfer.time?.length === 5 ? `${transfer.time}:00` : transfer.time
        const transferBooking = transfer.date && dealOptionsTime && transfer.redemptionLocationId ? {
          booking_date: transfer.date,
          booking_time: dealOptionsTime,
          offer_id: experience.id,
          option_id: transfer.option.id,
          location_id: transfer.redemptionLocationId,
          customers: transfer.travellers.adults + (transfer.travellers.children ?? 0),
        } : undefined

        // if member price is available, use it, otherwise use the price
        let price = transfer.option.price
        let memberPriceDiscountAmount = 0
        if (isLuxPlusDiscountedExperiencesEnabled && checkoutWithMemberPrice && transfer.option.memberPrice > 0) {
          price = transfer.option.memberPrice
          memberPriceDiscountAmount = Math.max(transfer.option.price - price, 0)
        }

        const itemDiscounts = filterItemDiscounts({ itemDiscounts: transferItemDiscounts.itemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })

        // Multiple bedbank cart items (that belong to the same session) become grouped into a
        // single formatted bedbank item; we must normalise our parent cart item ID references
        // accordingly.
        const parentCartItemIds = item.bundledItemIds
          ? [...new Set(item.bundledItemIds.map(itemId => bedbankItemIdToLeadItemId.get(itemId) ?? itemId))]
          : undefined

        const selectedExtras = getSelectedTransferExtras(transfer.extras)?.map(extra => ({
          id: extra.id,
          title: extra.title,
          quantity: extra.quantity,
          price: extra.price,
        }))
        const formattedTransferItem = {
          type: ITEM_TYPE_EXPERIENCE,
          provider_offer_id: experience.id,
          brand: config.BRAND,
          transaction_key: transactionKey,
          total: price + getTransferExtrasTotal(transfer.extras),
          ...(selectedExtras ? { extras: selectedExtras } : {}),
          taxes_and_fees: transfer.option.taxesAndFees,
          is_accom_bundle: item.isAccomBundle,
          bundle_optin: item.bundleOptin ?? false,
          parent_cart_item_ids: parentCartItemIds,
          tickted: experience.ticketed,
          title: experience.name,
          booking_start_date: fullDateTime,
          categories: experience.categories,
          cancellation_policies: experience.cancellationPolicies.length > 0 ? {
            timezoneOffset: `${new Date().getTimezoneOffset()}`,
            refundPolicies: experience.cancellationPolicies.map(policy => ({
              id: policy.id,
              periodLabel: policy.periodLabel,
              periods: policy.periods,
              value: policy.value,
              groupId: policy.groupId,
            })),
          } : undefined,
          le_exclusive: experience.leExclusive,
          meta_data: item.metaData,
          special_requests: transfer.specialRequests,
          ...formattedTravellers && {
            traveller_info: {
              customer: formattedTravellers?.customer,
            },
          },
          pickup_point_name: transfer.pickupPoint?.name ?? (transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.name : experience.airport?.name),
          pickup_point_id: transfer.pickupPoint?.id ?? (transfer.type === 'HOTEL-TO-AIRPORT' ? transfer.hotel.googlePlaceId : experience.airport?.code),
          dropoff_point_name: transfer.dropoffPoint?.name ?? (transfer.type === 'HOTEL-TO-AIRPORT' ? (transfer.isHomePickup ? transfer.airport?.name : experience.airport?.name) : transfer.hotel.name),
          dropoff_point_id: transfer.dropoffPoint?.id ?? (transfer.type === 'HOTEL-TO-AIRPORT' ? (transfer.isHomePickup ? transfer.airport?.code : experience.airport?.code) : transfer.hotel.googlePlaceId),
          // LED provider experiences use redemption_location_id to identify the booking location
          ...(transfer.redemptionLocationId && { redemption_location_id: transfer.redemptionLocationId }),
          deal_options: {
            adults: transfer.travellers.adults,
            children: transfer.travellers.children ?? 0,
            ...(transfer.travellers.infants !== undefined && {
              infants: transfer.travellers.infants,
            }),
            child_seats: transfer.travellers.childSeats ?? 0,
            booster_seats: transfer.travellers.boosterSeats ?? 0,
            option: transfer.option,
            ...(transfer.date && { date: transfer.date }),
            ...(dealOptionsTime && { time: dealOptionsTime }),
            flight_number: transfer.flightNumber,
            type: transfer.type,
            bags: transfer.baggage?.bags ?? 0,
            oversized_bags: transfer.baggage?.oversizedBags ?? 0,
            is_home_pickup: !!transfer.isHomePickup,
            is_loyalty_transfer: !!item.isLoyaltyTransfer,
            has_loyalty_benefit: !!item.hasLoyaltyBenefit,
            ...((transfer.pickupPoint || transfer.dropoffPoint) && {
              route: {
                pickupPoint: transfer.pickupPoint,
                dropoffPoint: transfer.dropoffPoint,
              },
            }),
          },
          ...(transferBooking && { booking: transferBooking }),
          ticket: {
            date: fullDateTime,
            // time is optional so we need separate fields to know whether it was set or not
            day: transfer.date,
            time: transfer.time,
            fareType: transfer.option.name,
            identifier: transfer.option.id,
            productId: transfer.option.id,
            type: transfer.type,
            pickup_date: null,
            pickup_time: null,
            surchargeAUD: 0,
            surchargeIDR: 0,
          },
          book_by_date: transfer.option?.bookByDate,
          ...((transfer.option?.discounts?.app.amount ?? 0) > 0 && {
            app_discount_amount: transfer.option.discounts?.app.amount,
            app_discount_percent: transfer.option.discounts?.app.percentage,
          }),
          member_discount_amount: memberPriceDiscountAmount,
          item_discounts: itemDiscounts.map(d => formatItemDiscount(d, transactionKey)),
          source_app: sourceApp,
        }

        return formattedTransferItem
      }

      return null
    })
  },
)

const getFormattedBundleAndSaveItems = createSelector(
  (state: App.State) => getBundleAndSaveItemViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.auth.account,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (views, travellerForms, account, specialRequests, promoCodeItemDiscounts) => {
    const data = views.data

    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    return data.flatMap((view) => {
      return view.hotels.map(hotelView => {
        const hotelViewPrice = hotelView.totals.price
        const newPrice = view.item.dates[hotelView.offer.id]?.newPrice
        const newSurcharge = view.item.dates[hotelView.offer.id]?.newSurcharge
        const newExtraGuestSurcharge = view.item.dates[hotelView.offer.id]?.newExtraGuestSurcharge

        const itemDiscounts = filterItemDiscounts({ itemDiscounts: promoCodeItemDiscounts.itemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [view.item.itemId] } })
        const bundleSaveTransactionKey = uuidV4()
        // TODO: We don't have an official type for order item creation, create it
        const orderItem: any = {
          bundle_offer_id: view.bundleOffer.id,
          bundle_package_id: view.bundlePackage.id,
          reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
          check_in: moment.utc(hotelView.checkIn).format(ISO_DATE_FORMAT),
          check_out: moment.utc(hotelView.checkOut).format(ISO_DATE_FORMAT),
          number_of_nights: hotelView.duration,
          number_of_adults: view.occupancy.adults,
          number_of_children: view.occupancy.children,
          number_of_infants: view.occupancy.infants,
          number_of_teenagers: view.occupancy.teenagers,
          children_ages: view.occupancy.childrenAge ?? [],
          rate_plan_id: hotelView.package.roomRate?.id,
          guest_first_name: primaryTraveller?.data.firstName,
          guest_last_name: primaryTraveller?.data.lastName,
          cart_item_id: view.item.itemId,
          offer_id: hotelView.offer.id,
          package_id: hotelView.package.id,
          room_rate_id: hotelView.package.roomRate?.id,
          transaction_key: bundleSaveTransactionKey,
          offer_booking_type: 'reservation',
          offer_type: hotelView.offer.type,
          offer_name: hotelView.offer.name,
          package_name: hotelView.package.name,
          offer_slug: view.bundleOffer.slug,
          price: newPrice || hotelViewPrice,
          value: hotelView.package.value,
          surcharge: newSurcharge ?? hotelView.totals.surcharge - hotelView.totals.extraGuestSurcharge,
          guest_special_requests: specialRequests[view.item.itemId],
          extra_guest_surcharge: newExtraGuestSurcharge ?? hotelView.totals.extraGuestSurcharge,
          property_fees: hotelView.totals.otherFees?.propertyFees,
          localizedPrices: hotelView.package.trackingPrice,
          customer_email: primaryTraveller?.data.email,
          customer_name: account?.fullName,
          room_rate_amount: undefined,
          item_discounts: itemDiscounts.map(d => formatItemDiscount(d, bundleSaveTransactionKey)),
        }

        if (OFFER_DYNAMIC_PRICING_TYPES.includes(hotelView.offer.type)) {
          orderItem.room_rate_amount = newPrice || hotelViewPrice
        }

        return orderItem
      })
    })
  },
)

const getFormattedTourV2Items = createSelector(
  (state: App.State) => getTourV2CheckoutItems(state),
  (state: App.State) => getTourV2ExperienceItems(state),
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (
    tourV2Items,
    tourExperiencesItems,
    specialRequests,
    offers,
    travellerForms,
    schemaData,
    promoCodeItemDiscounts,
    checkoutWithMemberPrice,
    isTourDateChange,
    isPostPurchaseTourOptionalExperience,
  ): App.WithDataStatus<Array<any>> => {
    const groupedTourItems = groupBy(tourV2Items, (item: App.Checkout.TourV2Item) => item.purchasableOption.fkDepartureId)
    if (groupedTourItems.size === 0) {
      return {
        data: [],
        hasRequiredData: tourV2Items.filter(isTourV2Item).length === 0,
      }
    }
    if (!promoCodeItemDiscounts.hasRequiredPromoCodeData) {
      return { data: [], hasRequiredData: false }
    }

    // Don't send tour items for post-purchase optional experience purchases
    // Only the tour_optional_experience item should be sent (handled by getFormattedTourExperienceItems)
    if (isPostPurchaseTourOptionalExperience) {
      return { data: [], hasRequiredData: true }
    }

    if (isTourDateChange) {
      const formattedTourV2Items = Array.from(groupedTourItems.values())
        .map(itemGroup => {
          const offer = offers[itemGroup[0].offerId] as App.Tours.TourV2Offer
          return formatTourV2ItemGroupDateChanges(itemGroup, offer, checkoutWithMemberPrice)
        })
      return { data: formattedTourV2Items, hasRequiredData: true }
    }

    if (schemaData && travellerForms.length > 0) {
      const formattedTourV2Items = Array.from(groupedTourItems.values())
        .map(itemGroup => {
          const offer = offers[itemGroup[0].offerId] as App.Tours.TourV2Offer
          const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
          if (!offer || !primaryTraveller) {
            return { data: EmptyArray, hasRequiredData: false }
          }
          return formatTourV2ItemGroup(
            itemGroup,
            tourExperiencesItems,
            specialRequests,
            travellerForms,
            primaryTraveller,
            offer,
            schemaData.accommodationFlightSchema,
            checkoutWithMemberPrice,
            promoCodeItemDiscounts.itemDiscounts,
          )
        })
      return { data: formattedTourV2Items, hasRequiredData: true }
    }
    return {
      data: [],
      hasRequiredData: tourV2Items.filter(isTourV2Item).length === 0,
    }
  },
)

const getFormattedTourExperienceItems = createSelector(
  (state: App.State) => getTourV2ExperienceItems(state),
  (state: App.State) => getIsPostPurchaseTourOptionalExperience(state),
  (tourExperienceItems: Array<App.Checkout.TourV2ExperienceItem>, isPostPurchaseTourExperience: boolean): App.WithDataStatus<Array<any>> => {
    return {
      data: isPostPurchaseTourExperience ? [{
        totalprice: sum(tourExperienceItems.map(item => item.total)),
        optional_experiences: tourExperienceItems.map(formatTourExperienceItem),
        type: 'tour_optional_experience',
        transaction_key: uuidV4(),
      }] : [],
      hasRequiredData: true,
    }
  },
)

export const getFormattedCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getCheckoutCruiseOfferView(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCruiseItemDiscounts(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getShouldEarnLoyaltyPoints(state),
  (state: App.State) => getBookingSessionData(state),
  (state: App.State) => getShouldUseCruiseMemberPrice(state),
  (items, offerViewsWithStatus, travellerForms, { itemDiscounts: cruiseItemDiscounts, hasRequiredPromoCodeData }, formSchemaData, shouldEarnLoyaltyPoints, sessionData, shouldUseCruiseMemberPrice): App.WithDataStatus<Array<any>> => {
    const cruiseItems = items.filter(isCruiseItem)
    if (cruiseItems.length === 0) {
      return { data: [], hasRequiredData: true }
    }

    const formSchema = formSchemaData?.accommodationFlightSchema
    if (!travellerForms || !offerViewsWithStatus.hasRequiredData || !formSchema) {
      return { data: [], hasRequiredData: false }
    }

    const personTitles = sessionData.personTitles ?? []
    const offerViews = offerViewsWithStatus.data
    const result = offerViews.flatMap(offerView => {
      const { itemViews } = offerView
      return itemViews.map(itemView => {
        const { departure } = itemView
        const itemTravellers = travellerForms.filter(form => formSchema.properties[form.id]?.relatedItem?.id === itemView.item.itemId)
        const itemDiscounts = filterItemDiscounts({
          itemDiscounts: cruiseItemDiscounts,
          lookup: { specificType: 'cart-id-based', cartItemIds: [itemView.item.itemId] },
        })

        if (!departure || !itemTravellers.length || !hasRequiredPromoCodeData) { return null }

        return formatCruiseItem(itemView, itemTravellers, itemDiscounts, shouldEarnLoyaltyPoints, personTitles, shouldUseCruiseMemberPrice)
      })
    }).filter(excludeNullOrUndefined)

    return { data: result, hasRequiredData: result.length === cruiseItems.length }
  },
)

const getFormattedInsuranceItems = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (state: App.State) => getOrderSubscriber(state),
  (state: App.State) => getInsuranceItemDiscounts(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => getCheckoutSelectedQuotes(state),
  (state: App.State) => state.checkout.cart.cartId,
  (state: App.State) => state.auth.account,
  (state: App.State) => getCheckoutQuoteSessionId(state),
  (
    itemView,
    travellers,
    insuranceTravellers,
    subscriber,
    insuranceItemDiscounts,
    isCruiseCheckout,
    isPostPurchaseInsurance,
    selectedQuotes,
    cartId,
    account,
    quoteSessionId,
  ) => {
    const primaryTraveller = travellers.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

    const viewItemsWithQuotes = itemView.data.filter(viewItem => viewItem.quoteId)

    const hasItemsRequiringTravellerForms = viewItemsWithQuotes.some(viewItem => !shouldUseInsuranceItemCustomTravellers(
      viewItem.item,
      isCruiseCheckout,
      isPostPurchaseInsurance,
    ))

    // Custom traveller items can be submitted without checkout traveller forms.
    if (!primaryTraveller && hasItemsRequiringTravellerForms) {
      return []
    }

    if (!insuranceItemDiscounts.hasRequiredPromoCodeData) {
      return []
    }

    // views must have a quote associated with them -
    // those without a quote id are most likely post purchase updates and are handled via
    // the specific post purchase operation
    return viewItemsWithQuotes.map(viewItem => {
      // getInsuranceItemsView already resolves totals.memberPrice to 0 for any item this buyer
      // isn't genuinely eligible for (whether via the legacy flag or flight-CFMR's own real
      // membership check) - so a per-item value check is the correct source of truth for what
      // gets charged, not the single flag-based useMemberPrice, which would otherwise still
      // submit the full price for a flight CFMR item whenever the legacy flag happens to be off.
      const total = viewItem.totals.memberPrice > 0 ? viewItem.totals.memberPrice : viewItem.totals.price
      const isPassportCardItem = viewItem.item.productId === PRODUCT_ID_PASSPORT_CARD
      const quote = selectedQuotes[viewItem.item.itemId]
      const {
        travellersDetails,
        isUsingCustomTravellers,
      } = getCheckoutInsuranceOrderTravellerDetails(
        viewItem.item,
        travellers,
        isCruiseCheckout,
        isPostPurchaseInsurance,
        quote?.requestedTravellers,
      )

      if (isPassportCardItem) {
        const fallbackAddress: App.PassportCardTravellerAddress = {
          country: 'AU',
          state: primaryTraveller?.data.state || 'Unknown',
          street: primaryTraveller?.data.address || 'Unknown',
          city: primaryTraveller?.data.city || 'Unknown',
          postcode: primaryTraveller?.data.postcode || '0000',
        }
        // Avoid inflating the PassportCard Accept payload with unrelated checkout
        // traveller forms; PassportCard rejects Accept when it diverges from Quote.
        const fallbackTravellerForms = getPassportCardFallbackTravellerForms(travellers, insuranceTravellers)
        const fallbackPassportCardTravellers: Array<App.PassportCardInsuranceTraveller> = fallbackTravellerForms.map((traveller, index) => ({
          firstName: traveller.data.firstName || '',
          lastName: traveller.data.lastName || '',
          ageRange: traveller.id.startsWith('adult-') ? 'adult' : 'child',
          email: traveller.data.email || '',
          phonePrefix: traveller.data.phonePrefix || '',
          phone: traveller.data.phone || '',
          dateOfBirth: traveller.data.dateOfBirth || '',
          ...(index === 0 ? {
            address: {
              ...fallbackAddress,
              street: traveller.data.address || fallbackAddress.street,
            },
          } : {}),
        }))

        const passportCardTravellers = viewItem.item.passportCardTravellers?.length
          ? viewItem.item.passportCardTravellers
          : fallbackPassportCardTravellers

        const policyHolderPhone = (passportCardTravellers[0]?.phonePrefix && passportCardTravellers[0]?.phone)
          ? `+${passportCardTravellers[0].phonePrefix}${passportCardTravellers[0].phone}`
          : ''
        const insuredPersons = passportCardTravellers.slice(1)
        const policyAddress = passportCardTravellers[0]?.address
        const parsedPolicyStreet = parseStreetNumber(policyAddress?.street || fallbackAddress.street || '')

        return {
          type: ITEM_TYPE_INSURANCE,
          provider: 'passport_card',
          product_id: viewItem.item.productId,
          quote_id: viewItem.quoteId,
          // Correlation id threaded to svc-order -> svc-insurance. Must equal the
          // session_id used at PassportCard quote time (getCheckoutQuoteSessionId).
          session_id: quoteSessionId,
          // proposal_id and the optional_benefits key are the same value and must stay that
          // way: PassportCard rejects Accept when it diverges from Quote. Both read the
          // proposal resolved from the live quote, so an edited coverage window is purchased
          // as displayed rather than against the proposal the item was first stamped with.
          proposal_id: viewItem.proposalId ?? '',
          optional_benefits: viewItem.item.passportCardOptionalBenefitCodes?.length && viewItem.proposalId
            ? { [viewItem.proposalId]: viewItem.item.passportCardOptionalBenefitCodes }
            : undefined,
          brand: config.BRAND,
          transaction_key: viewItem.item.transactionKey,
          policy_holder_firstname: passportCardTravellers[0]?.firstName || '',
          policy_holder_surname: passportCardTravellers[0]?.lastName || '',
          travellers_details: passportCardTravellers.map(traveller => ({
            firstname: traveller.firstName,
            surname: traveller.lastName,
            age_range: traveller.ageRange,
          })),
          consumer_details: {
            salute: passportCardTravellers[0]?.title || primaryTraveller?.data.title || 'Mr',
            gender: '', // RODO: required for now, remove later
            firstName: passportCardTravellers[0]?.firstName || '',
            middleName: '',
            lastName: passportCardTravellers[0]?.lastName || '',
            email: passportCardTravellers[0]?.email || '',
            phone: policyHolderPhone,
            dateOfBirth: passportCardTravellers[0]?.dateOfBirth || '',
            personalId: account?.memberId || primaryTraveller?.id || viewItem.item.itemId,
          },
          insured_persons: insuredPersons.map(traveller => ({
            firstName: traveller.firstName,
            middleName: '',
            lastName: traveller.lastName,
            gender: '', // TODO: required for now, remove later
            dateOfBirth: traveller.dateOfBirth,
          })),
          address: {
            country: policyAddress?.country || fallbackAddress.country || 'AU',
            state: policyAddress?.state || fallbackAddress.state || '',
            street: parsedPolicyStreet.streetName,
            street2: policyAddress?.unitNumber || '',
            streetNumber: parsedPolicyStreet.streetNumber,
            city: policyAddress?.city || fallbackAddress.city || '',
            zip: policyAddress?.postcode || fallbackAddress.postcode || '',
          },
          insurance_type: viewItem.item.insuranceType,
          total: floatify(total),
          subscriber,
          mobile_only_price: !!viewItem.mobileAppPrice,
          item_discounts: insuranceItemDiscounts.itemDiscounts.map(d => formatItemDiscount(d, viewItem.item.transactionKey)),
          flight_journeys_keys: viewItem.item.flightJourneysKeys,
          bedbank_room_ids: viewItem.item.bedbankRoomIds,
        }
      }

      // Validate residual quote/order traveller mismatches. The quote and traveller
      // schema should already be aligned before this payload is built, so any hit
      // here points at a divergence bug. Log-only: the item still submits, and the
      // over-submission direction additionally raises an alerting signal.
      const validationResult = validateInsuranceTravellerCount(
        quote,
        travellersDetails as Array<{ firstname: string | undefined; surname: string | undefined; age_range: string }>,
        viewItem.item.itemId,
        viewItem.item.insuranceType ?? 'unknown',
        !!isUsingCustomTravellers,
        isCruiseCheckout,
      )

      const mismatchLogContext = {
        cartId,
        travellerFormsCount: travellers.length,
        customTravellersCount: viewItem.item.travellers?.length ?? 0,
        hasItemsRequiringTravellerForms,
        isUsingCustomTravellers,
        quoteStartDate: quote?.startDate,
        quoteEndDate: quote?.endDate,
        quoteTotal: quote?.total,
      }

      if (!validationResult.isValid && validationResult.mismatch) {
        logInsuranceTravellerMismatch(validationResult.mismatch, mismatchLogContext)

        // Over-submission is the fatal direction: the provider receives the
        // insured list verbatim and rejects a longer-than-quoted one AFTER
        // payment, auto-cancelling and refunding the paid item. The dedicated
        // error-level signal exists to alarm on so the divergence gets analysed
        // and fixed manually.
        if (isInsuranceTravellerOverSubmission(validationResult)) {
          logInsuranceTravellerOverSubmission(validationResult.mismatch, mismatchLogContext)
        }
      } else if (validationResult.compositionMismatch) {
        logInsuranceTravellerCompositionMismatch(validationResult.compositionMismatch, mismatchLogContext)
      }

      return {
        type: ITEM_TYPE_INSURANCE,
        product_id: viewItem.item.productId,
        quote_id: viewItem.quoteId,
        brand: config.BRAND,
        transaction_key: viewItem.item.transactionKey,
        policy_holder_firstname: isUsingCustomTravellers && !primaryTraveller ? travellersDetails[0]?.firstname : primaryTraveller?.data.firstName ?? '',
        policy_holder_surname: isUsingCustomTravellers && !primaryTraveller ? travellersDetails[0]?.surname : primaryTraveller?.data.lastName ?? '',
        travellers_details: travellersDetails,
        insurance_type: viewItem.item.insuranceType,
        total: floatify(total),
        subscriber,
        mobile_only_price: !!viewItem.mobileAppPrice,
        item_discounts: insuranceItemDiscounts.itemDiscounts.map(d => formatItemDiscount(d, viewItem.item.transactionKey)),
        flight_journeys_keys: viewItem.item.flightJourneysKeys,
        bedbank_room_ids: viewItem.item.bedbankRoomIds,
      }
    })
  },
)

const getFormattedBookingProtectionItems = createSelector(
  (state: App.State) => getBookingProtectionItems(state),
  (state: App.State) => getBookingProtectionDetails(state),
  (bookingProtectionItems, bookingProtectionDetails): Array<App.Checkout.BookingProtectionDetails> | [] => {
    if (!bookingProtectionDetails) return []

    return bookingProtectionItems.map(item => ({
      type: ITEM_TYPE_BOOKING_PROTECTION,
      transaction_key: item.transactionKey,
      ...bookingProtectionDetails,
    }))
  },
)

export const getFormattedGiftCardItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => getGiftCardItemDiscounts(state),
  (account, items, giftCardItemDiscounts) => {
    const allItemDiscounts = giftCardItemDiscounts.hasRequiredPromoCodeData ? giftCardItemDiscounts.itemDiscounts : EmptyArray
    return items.map((item) => {
      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })

      return {
        type: ITEM_TYPE_GIFT_CARD,
        brand: config.BRAND,
        transaction_key: item.transactionKey,
        gift_card_value: item.amount,
        customer_email: account.email,
        customer_name: account.fullName,
        customer_phone: item.content.fromPhone || account.phone,
        customer_phone_prefix: item.content.fromPhonePrefix || account.phonePrefix,
        personalised: {
          method: item.deliveryMethod,
          to: item.content.recipientName,
          from: item.content.fromName,
          message: item.content.message,
          email_of_recipient: item.content.recipientEmail,
          is_blank_message: !item.content.message,
        },
        item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
        product: item.product ? {
          name: item.product.name,
          items: item.product.items,
          imageId: item.product.image.id,
          imageUrl: item.product.image.url,
          type: item.product.type,
          url: item.product.url,
          location: item.product.location,
        } : undefined,
      }
    })
  },
)

export function formatCartItems(
  cartState: App.CheckoutCartState,
  connectedRoomOfferIds: Array<string> = [],
  offers: Record<string, App.AnyOffer>,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Record<string, App.AnyJourney>,
  account: App.AuthAccount,
  isTravellerFormNeeded: boolean,
  formSchemaRequest: { items: Array<any> },
  formattedInstantBookingHotelItems: App.WithDataStatus<Array<unknown>>,
  formattedTourV2Items: App.WithDataStatus<Array<unknown>>,
  bedbankItemIdToLeadItemId: Map<string, string>,
  formattedExperienceItems: App.WithDataStatus<Array<ExperienceOrderCreationItem>>,
  formattedTransferItems: Array<unknown>,
  formattedInsuranceItems: Array<unknown>,
  getFormattedBookingProtectionItems: Array<unknown>,
  formattedCruiseItems: App.WithDataStatus<Array<unknown>>,
  formattedBundleAndSaveItems: Array<unknown>,
  formattedGiftCardItems: Array<unknown>,
  formattedVillaItems: Array<unknown>,
  formattedLuxPlusSubscriptionItems: Array<FormattedOrderSubscriptionItem>,
  formattedSubscriptionJoinFeeItems: Array<FormattedOrderSubscriptionItem>,
  checkoutWithMemberPrice: boolean,
  formattedTourExperienceItems: App.WithDataStatus<Array<unknown>>,
  existingOrderTraveller: App.ExistingOrderBedbankTraveller | undefined,
  promoCodeItemDiscounts: PromoItemDiscountsWithStatus,
  formattedLoyaltyLoungepassItems: Array<ExperienceOrderCreationItem>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  formattedMerchandiseItems: Array<MerchandiseOrderCreationItem>,
  formattedLoyaltyPurchasePointsItems: Array<LoyaltyPurchasePointsOrderCreationItem>,
  formattedMerchandiseShippingItems: App.WithDataStatus<Array<MerchandiseShippingOrderCreationItem>>,
  cartHasPropertyCollectItem: boolean,
  regionCode: string,
  markupSource?: ChannelMarkupSource,
  sessions?: Record<string, {
    data: App.BedbankEnquiry | null
    state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
  }>,
  accommodationSessions?: App.CheckoutState['accommodationSessions'],
  isLuxPlusPriced: boolean = false,
  propertyOfferMappings: App.OfferState['propertyOfferMappings'] = {},
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  overlappingRoomGuestNamesEnabled = false,
  includeFlashOverlappingRoomGuests = false,
): App.WithDataStatus<Array<any>> {
  const { itemDiscounts: allItemDiscounts, hasRequiredPromoCodeData } = promoCodeItemDiscounts

  if (
    isTravellerFormNeeded &&
    formSchemaRequest.items.length > 0 &&
    !travellerForms.some(form => form.id === PRIMARY_TRAVELLER_FORM_ID) ||
    !promoCodeItemDiscounts.hasRequiredPromoCodeData
  ) {
    return { hasRequiredData: false, data: [] }
  }

  let hasRequiredData = true

  const { items, specialRequests, arrivalFlightNumber } = cartState
  const eligibleRoomGuestItems = getRoomGuestItems(items.filter(isAccommodationItem), offers)
  const collectCustomerOverlappingRoomGuests = !config.agentHub.isEnabled && overlappingRoomGuestNamesEnabled
  const includeFlashRoomGuests = config.agentHub.isEnabled || includeFlashOverlappingRoomGuests
  const mappingRoomGuestItems = getMappedRoomGuestEligibleItems(
    eligibleRoomGuestItems,
    offers,
    overlappingRoomGuestNamesEnabled,
    includeFlashRoomGuests,
  )
  const roomGuestItems = getCheckoutRoomGuestItems(
    items.filter(isAccommodationItem),
    offers,
    config.agentHub.isEnabled,
    bedbankOffers,
    propertyOfferMappings,
    collectCustomerOverlappingRoomGuests,
    includeFlashOverlappingRoomGuests,
  )
  const propertyIdByItemId = getRoomGuestPropertyIdByItemId(
    mappingRoomGuestItems,
    offers,
    bedbankOffers,
    propertyOfferMappings,
  )
  if (
    overlappingRoomGuestNamesEnabled &&
    isRoomGuestPropertyMappingPending(mappingRoomGuestItems, offers, bedbankOffers, propertyOfferMappings)
  ) {
    hasRequiredData = false
  }
  const defaultFirstRoomToPrimary = config.agentHub.isEnabled ||
    shouldDefaultFirstCollectedRoomToPrimary(
      mappingRoomGuestItems,
      roomGuestItems,
      propertyIdByItemId,
    )

  const formattedBNBLHotelItems = items
    .filter(isBNBLLEHotelItem)
    .map(item => {
      const offer = offers[item.offerId] as App.Offer
      if (!offer) {
        hasRequiredData = false
        return null
      }

      return formatLeHotelItem(
        item,
        travellerForms,
        specialRequests[item.itemId],
        offer,
        undefined,
        account,
        checkoutWithMemberPrice,
        connectedRoomOfferIds,
        undefined,
        undefined,
        allItemDiscounts,
        markupSource,
      )
    })

  const formattedTourV1Items = items
    .filter(isTourV1Item)
    .map((item, idx) => {
      const offer = offers[item.offerId] as App.Offer
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts

      if (!offer) {
        hasRequiredData = false
        return null
      }

      if (travellerForms[idx]) {
        const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'cart-id-based', cartItemIds: [item.itemId] } })
        return formatLeTourV1Item(item, travellerForms[idx], specialRequests[item.itemId], offer, account, itemDiscounts)
      }
    })

  const formattedCarHireItems = items
    .filter(isCarHireItem)
    .map((item) => formatCarHireItem(item, travellerForms, arrivalFlightNumber, allItemDiscounts))

  // Bedbank cart room items need to be grouped together
  // Until the implementation changes on `svc-order`
  // As of now, there will only ever be 1 group
  const bedbankItems = items.filter(isBedbankItem)
  const groupedBedbankItems = [...groupBy(bedbankItems, item => item.sessionId).values()]

  const formattedBedbankItems = groupedBedbankItems.map(group => {
    const firstItem = group[0]
    // For bundled experience/transfer items, we must ensure they can consistently resolve to this
    // "lead" item ID.
    const leadItemId = bedbankItemIdToLeadItemId.get(firstItem.itemId) ?? firstItem.itemId
    const offer = offers[firstItem.offerId] as App.BedbankOffer
    if (!offer) {
      hasRequiredData = false
      return null
    }
    // Under v2 the persisted channel markup is resolved from the enquiry session's totals, the same
    // inputs the displayed price was marked from. Until that session has settled (`data` is null
    // while 'updating'/'updated') the identity would be evaluated without a price and could disagree
    // with what the customer saw, so the order is not ready. Gate on `channelMarkupId`, not merely
    // `sessionState`: the latter is truthy whenever a v2 config is loaded even when no campaign is
    // session-viable, and with no resolved campaign the offer round can never match (user conditions
    // don't re-evaluate per offer), so no markup is ever persisted — blocking those carts (e.g. on a
    // permanently unsettled/errored session) would regress v1/no-markup behaviour for no benefit.
    const hasSettledSession = !!(firstItem.sessionId && sessions?.[firstItem.sessionId]?.data?.totals)
    if (markupSource?.mode === 'v2' && markupSource.channelMarkupId && !hasSettledSession) {
      hasRequiredData = false
    }
    const requests = group.map(item => item.itemId).map(id => specialRequests?.[id] ?? '').join(',')
    const itemDiscounts = filterItemDiscounts({
      itemDiscounts: allItemDiscounts,
      lookup: { specificType: 'cart-id-based', cartItemIds: group.map(item => item.itemId) },
    })

    return formatBedbankItem(leadItemId, firstItem, group, requests, travellerForms, schemaData?.accommodationFlightSchema, roomGuestItems, itemDiscounts, cartHasPropertyCollectItem, regionCode, offer, markupSource, existingOrderTraveller, sessions, checkoutWithMemberPrice, defaultFirstRoomToPrimary, overlappingRoomGuestNamesEnabled)
  }).filter(Boolean)

  // One payload per occupancy — do not group by session the way bedbank does.
  const formattedAccommodationV2Items = items.filter(isAccommodationV2Item).map((item) => {
    if (!item.sessionId) {
      hasRequiredData = false
      return null
    }
    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
    const itemDiscounts = filterItemDiscounts({
      itemDiscounts: allItemDiscounts,
      lookup: { specificType: 'accommodationAndExperiences', itemIds: [item.itemId] },
    })
    const sessionRecord = accommodationSessions?.[item.sessionId]
    return formatAccommodationV2Item(
      item,
      primaryTraveller,
      specialRequests[item.itemId] ?? '',
      roomGuestItems,
      itemDiscounts,
      isAccommodationV2PropertyCollectRecord(sessionRecord) ? regionCode : undefined,
      sessionRecord?.data,
    )
  }).filter(Boolean)

  const formattedFlightItems = items
    .filter(isFlightItem)
    .reduce<Array<unknown>>((formattedItems, item) => {
      let flightJourneys: Array<App.JourneyV2> = []
      const isFlightDateChange = !!item.isFlightDateChange

      if (item.searchId) {
        let keys: Array<string> = []
        if (isFlightDateChange) {
          keys = item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight?.journeyId))
        } else {
          keys = item.flights.map(flight => getJourneyV2IdKey(flight.journeyId, item.searchId!, flight.fareFamily?.id))
        }
        flightJourneys = keys.map(key => journeys[key] as App.JourneyV2)
      }

      const hasAllJourneys = item.flights.length > 0 &&
        flightJourneys.length === item.flights.length &&
        flightJourneys.every(journey => !!journey)

      if (!hasAllJourneys || !item.hasFinalFare) {
        hasRequiredData = false
      }

      const data = hasAllJourneys ? [
        ...formattedItems,
        ...formatFlightItem(item, travellerForms, flightJourneys, allItemDiscounts, schemaData, isFlightDateChange, isLuxPlusPriced),
      ] : formattedItems

      return data
    }, [])

  // Check if data requirements satisfied
  hasRequiredData = hasRequiredData &&
    formattedInstantBookingHotelItems.hasRequiredData &&
    formattedTourV2Items.hasRequiredData &&
    formattedExperienceItems.hasRequiredData &&
    formattedCruiseItems.hasRequiredData &&
    formattedTourExperienceItems.hasRequiredData &&
    formattedMerchandiseShippingItems.hasRequiredData &&
    hasRequiredPromoCodeData

  const formattedPurchaseCreditItems = items
    .filter(isPurchaseCreditItem)
    .map((item) => ({
      type: ITEM_TYPE_PURCHASE_CREDIT,
      brand: config.BRAND,
      transaction_key: item.transactionKey,
      purchase_credit_value: item.amount,
      customer_email: account.email,
      customer_name: account.fullName,
    }))

  const formattedItems = [
    ...formattedInstantBookingHotelItems.data,
    ...formattedBNBLHotelItems,
    ...formattedTourV1Items,
    ...formattedTourV2Items.data,
    ...formattedBedbankItems,
    ...formattedAccommodationV2Items,
    ...formattedFlightItems,
    ...formattedExperienceItems.data,
    ...formattedTransferItems,
    ...formattedInsuranceItems,
    ...getFormattedBookingProtectionItems,
    ...formattedCruiseItems.data,
    ...formattedCarHireItems,
    ...formattedBundleAndSaveItems,
    ...formattedGiftCardItems,
    ...formattedVillaItems,
    ...formattedLuxPlusSubscriptionItems,
    ...formattedSubscriptionJoinFeeItems,
    ...formattedTourExperienceItems.data,
    ...formattedLoyaltyLoungepassItems,
    ...formattedMerchandiseItems,
    ...formattedMerchandiseShippingItems.data,
    ...formattedPurchaseCreditItems,
    ...formattedLoyaltyPurchasePointsItems,
  ]

  return {
    hasRequiredData,
    data: formattedItems,
  }
}

/**
 * Describes the rate the member upgraded *from*, so svc-order can work out what the free upgrade cost
 * the business. An LPC source is resolved from `property_id` + `room_rate_id`, a bedbank source from
 * the enquiry `session_id`; the other fields only appear in svc-order's logs. Returns nothing unless
 * an upgrade is actually applied, so a booking on the base rate sends no descriptor.
 */
function formatLoyaltyUpgradeSource(upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined) {
  if (!upgrade || upgrade.applied === false) return undefined

  return {
    service_type: upgrade.source.serviceType,
    offer_id: upgrade.source.serviceOfferId,
    property_id: upgrade.source.propertyId,
    room_id: upgrade.source.roomId,
    room_rate_id: upgrade.source.serviceRateId,
    package_id: upgrade.source.servicePackageId,
    session_id: upgrade.sourceBooking?.sessionId,
  }
}

function formatLeHotelItem(
  item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BNBLLEHotelItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  specialRequest: string,
  offer: App.Offer,
  instantBookingPricing: (App.Checkout.ItemViewTotals & {
    extraGuestSurchargesPayableAtProperty?: boolean
  }) | undefined,
  account: App.AuthAccount,
  checkoutWithMemberPrice: boolean,
  connectedRoomOfferIds: Array<string>,
  cartMode?: App.CheckoutCartMode,
  arrivalDetails?: App.ArrivalDetails,
  allItemDiscounts?: Array<App.Checkout.ItemDiscount>,
  markupSource?: ChannelMarkupSource,
  roomGuestIndex?: number,
  connectedRoomContactNames?: Array<string>,
  lereGroup?: string,
  isPrimaryGuestRoom?: boolean,
  defaultFirstRoomToPrimary = true,
  formSchema?: App.Checkout.FormSchema,
  uniquePrimaryRoomGuest = false,
) {
  const pkg = getEffectiveLeHotelPackage(item, offer)

  if (!pkg) {
    throw new Error(`Package with id ${item.packageId} and room rate id ${item.roomRateId} not found in offer ${offer.id}`)
  }

  // primaryTraveller will be absent on package upgrading
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const priceFields = {
    price: instantBookingPricing?.price ?? pkg.price ?? 0,
    memberPrice: offer.type === OFFER_TYPE_HOTEL
      ? (instantBookingPricing?.memberPrice || pkg.memberPrice) ?? 0
      : (instantBookingPricing?.memberPrice) ?? 0,
    taxesAndFees: instantBookingPricing?.taxesAndFees ?? pkg.taxesAndFees ?? 0,
    surcharge: instantBookingPricing?.surcharge ?? 0,
    extraGuestSurcharge: instantBookingPricing?.extraGuestSurcharge ?? 0,
    extraGuestSurchargesPayableAtProperty: instantBookingPricing?.extraGuestSurchargesPayableAtProperty ?? false,
  }
  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts ?? [], lookup: { specificType: 'accommodationAndExperiences', itemIds: [item.itemId] } })

  const offerSupportsLuxPlus = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA].includes(offer.type)

  // Channel markup for LPC: the displayed `price`/`lux_plus_price` carry the markup,
  // but svc-reservation validates the *un-marked* base rate. Send the marked price for
  // charging while keeping `room_rate_amount`/member base un-marked. When markup is not
  // applied (flag off / non-channel traffic) base === price, so behaviour is unchanged.
  // Mode-tagged source: matches the markup the mappers applied at display time (v1 or v2).
  // Evaluate on the un-marked base rates: the display mappers evaluated v2 conditions (e.g. a
  // `luxPlusPrice` bound) against the raw server prices *before* applying the markup, whereas
  // `priceFields` already carries it. Falls back to the (marked) price when no base is known.
  const markupPricing = {
    total: instantBookingPricing?.baseRoomRate || priceFields.price,
    luxPlusPrice: (instantBookingPricing?.baseMemberRoomRate || priceFields.memberPrice) || undefined,
  }
  const channelMarkupApplied = resolveChannelMarkupDetails(offer.id, offer.type, markupSource, markupPricing).isApplied
  const memberChannelMarkupApplied = resolveMemberChannelMarkupDetails(offer.id, offer.type, markupSource, markupPricing).isApplied
  const memberLuxPlusPrice = memberChannelMarkupApplied
    ? (instantBookingPricing?.baseMemberRoomRate ?? priceFields.memberPrice)
    : priceFields.memberPrice

  const newBookingFields = {
    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,
    offer_booking_type: 'reservation',
    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    number_of_nights: pkg.duration,
    value: pkg.value,
    localizedPrices: pkg.trackingPrice,
    price: checkoutWithMemberPrice && priceFields.memberPrice > 0 ? priceFields.memberPrice : priceFields.price,
    public_price: offerSupportsLuxPlus ? priceFields.price : undefined,
    lux_plus_price: offerSupportsLuxPlus && priceFields.memberPrice > 0 ? memberLuxPlusPrice : undefined,
    surcharge: priceFields.surcharge,
    extra_guest_surcharge: priceFields.extraGuestSurchargesPayableAtProperty ? 0 : priceFields.extraGuestSurcharge,
    extra_guest_surcharge_payable_at_property: priceFields.extraGuestSurchargesPayableAtProperty ? priceFields.extraGuestSurcharge : 0,
    lere_group: getLereDynamicPricingGroupForOffer(offer, lereGroup),
    reservation_type: item.reservationType,
  }

  if (item.newPrice !== undefined) {
    // accept a new price due to price change
    newBookingFields.price = item.newPrice
  }
  if (item.newSurcharge !== undefined) {
    newBookingFields.surcharge = item.newSurcharge
  }
  if (item.newExtraGuestSurcharge !== undefined) {
    newBookingFields.extra_guest_surcharge = item.newExtraGuestSurcharge
  }

  const selectDateFields = {
    ...(cartMode == 'select-date' && 'orderItemId' in item && {
      type: 'reservation',
      itemId: item.orderItemId,
      peak_period_surcharge: priceFields.surcharge,
      extra_guest_surcharge: priceFields.extraGuestSurcharge,
    }),
  }

  const arrivalDetailFields = {
    ...(arrivalDetails && {
      arrival_date: moment.utc(arrivalDetails.arrivalDate).format(ISO_DATE_FORMAT),
      arrival_time: arrivalDetails.arrivalTime,
      arrival_flight_number: arrivalDetails.arrivalFlightNumber,
    }),
  }

  let specialRequestWithConnectedRoomsMessage = specialRequest
  if (connectedRoomOfferIds.includes(offer.id)) {
    specialRequestWithConnectedRoomsMessage = addConnectRoomsSpecialRequestMessage(
      specialRequest,
      connectedRoomContactNames?.length ? connectedRoomContactNames : getContactName(primaryTraveller, account),
    )
  }

  const data: any = {
    // common fields
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,
    guest_special_requests: specialRequestWithConnectedRoomsMessage,
    customer_email: primaryTraveller?.data.email || account.email,
    customer_name: account.fullName,
    arrival_details: arrivalDetailFields,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(cartMode === 'select-date' ? selectDateFields : newBookingFields),
  }

  if (item.reservationType === RESERVATION_TYPE_BOOK_LATER && item.occupancy) {
    data.intended_occupancy = {
      number_of_adults: item.occupancy.adults,
      number_of_children: item.occupancy.children ?? 0,
      number_of_infants: item.occupancy.infants ?? 0,
      number_of_teenagers: item.occupancy.teenagers ?? 0,
      children_ages: item.occupancy.childrenAge ?? [],
    }
  }

  if (
    item.reservationType === RESERVATION_TYPE_BOOK_LATER &&
    item.upfrontExtraGuestSurcharge != null
  ) {
    // Carried only when the voice_ai_bnbl_v2 flag is on, so its presence gates v2; svc-order re-derives & validates the amount.
    data.bnbl_version = BNBL_VERSION_UPFRONT_SURCHARGE
    data.extra_guest_surcharge = item.upfrontExtraGuestSurcharge
    data.extra_guest_surcharge_payable_at_property =
      item.upfrontExtraGuestSurchargePayableAtProperty ?? 0
  }

  if (item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
    data.check_in = moment.utc(item.checkIn).format(ISO_DATE_FORMAT)
    data.check_out = moment.utc(item.checkOut).format(ISO_DATE_FORMAT)
    data.number_of_adults = item.occupancy.adults
    data.number_of_children = item.occupancy.children
    data.number_of_infants = item.occupancy.infants
    data.number_of_teenagers = item.occupancy.teenagers
    data.children_ages = item.occupancy.childrenAge ?? []
    data.rate_plan_id = pkg.roomRate?.id
    data.is_upgraded_rate = item.isUpgradedRate

    if (primaryTraveller) {
      const isRoomGuestNamesEnabled = typeof roomGuestIndex === 'number'
      const guestNames = getRoomGuestNames({
        itemId: item.itemId,
        primaryForm: primaryTraveller,
        travellerForms,
        formSchema,
        fallbackFirstName: primaryTraveller.data.firstName,
        fallbackLastName: primaryTraveller.data.lastName,
        isEnabled: isRoomGuestNamesEnabled,
        isPrimaryRoom: roomGuestIndex === 0 && defaultFirstRoomToPrimary,
      })

      data.guest_first_name = guestNames.firstName
      data.guest_last_name = guestNames.lastName
      if (uniquePrimaryRoomGuest) {
        if (isRoomGuestNamesEnabled) {
          data.is_primary = !!isPrimaryGuestRoom
        }
      } else if (config.agentHub.isEnabled) {
        data.is_primary = isSameRoomGuestName(
          guestNames.firstName,
          guestNames.lastName,
          primaryTraveller.data.firstName,
          primaryTraveller.data.lastName,
        )
      }
      data.guest_phone = (primaryTraveller.data.phonePrefix && primaryTraveller.data.phone) ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined
    }

    if (OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type)) {
      const instantBookingPrice = instantBookingPricing?.price ?? 0
      // For channel-marked LPC, send the un-marked base rate so svc-reservation's
      // strict-equality check passes; svc-order charges the marked price + surcharge.
      const baseRoomRate = channelMarkupApplied
        ? (instantBookingPricing?.baseRoomRate ?? instantBookingPrice)
        : instantBookingPrice
      data.room_rate_amount = item.newPrice || baseRoomRate
    }
  }

  // Persist the markup that priced this item so svc-order re-applies the same one it charges.
  const chargedMemberPrice = checkoutWithMemberPrice && priceFields.memberPrice > 0
  const channelMarkupIdentity = resolveChannelMarkupOrderIdentity(offer.id, offer.type, markupSource, markupPricing, { member: chargedMemberPrice })
  if (channelMarkupIdentity) {
    data.channel_markup_id = channelMarkupIdentity.channelMarkupId
    data.channel_markup_round_to_ten = true
    if (channelMarkupIdentity.version === 'v2') {
      // The specific markup within the campaign, so svc-order re-applies the exact one it charged.
      // svc-order names this field `channel_markup_discount_id`; it carries the client's `markupId`.
      data.channel_markup_discount_id = channelMarkupIdentity.markupId
    }
  }
  // Loyalty upgrade fields go out independently rather than as a group. Only an LPC source has a base
  // package id, so grouping them would drop the upgrade type from a bedbank-source booking. svc-order
  // stores each in its own column and validates none of them against the others.
  if (item.loyaltyAppliedUpgradeType) {
    data.applied_upgrade_type = item.loyaltyAppliedUpgradeType
  }
  if (item.loyaltyBasePackageId) {
    data.base_package_id = item.loyaltyBasePackageId
  }
  const loyaltyUpgradeSource = formatLoyaltyUpgradeSource(item.loyaltyPropertyRateUpgrade)
  if (loyaltyUpgradeSource) {
    data.loyalty_upgrade_source = loyaltyUpgradeSource
  }
  if (item.accommAvailabilitySessionId) {
    data.accomm_availability_session_id = item.accommAvailabilitySessionId
  }
  if (item.accommCampaignId) {
    data.accomm_campaign_id = item.accommCampaignId
  }
  if (item.accommBundleIds?.length) {
    data.accomm_bundle_ids = item.accommBundleIds
    data.bundle_optin = item.bundleOptin ?? false
  }
  if (item.bypassRegionFilter) {
    data.bypass_region = true
    if (item.bypassRegionSettingId) {
      data.bypass_region_setting_id = item.bypassRegionSettingId
    }
  }
  return data
}

function formatLeTourV1Item(
  item: App.Checkout.LETourV1Item,
  form: App.Checkout.TravellerForm,
  specialRequest: string,
  offer: App.Offer,
  account: App.AuthAccount,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const pkg = offer.packages.find(pkg => pkg.id === item.packageId)
  if (!pkg) {
    throw new Error(`Package with id ${item.packageId} not found in offer ${offer.id}`)
  }
  const dob = form.data.dob || form.data.dateOfBirth
  const phone = (form.data.phonePrefix && form.data.phone) ? `${form.data.phonePrefix}${form.data.phone}` : undefined

  return {
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,

    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,

    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    value: pkg.value,
    price: pkg.price,
    localizedPrices: pkg.trackingPrice,
    number_of_days: pkg.duration,

    offer_booking_type: 'reservation',
    reservation_type: RESERVATION_TYPE_INSTANT_BOOKING,
    guest_special_requests: specialRequest,

    customer_email: account.email,
    customer_name: account.fullName,

    start_date: moment.utc(item.startDate).format(ISO_DATE_FORMAT),
    end_date: moment.utc(item.endDate).format(ISO_DATE_FORMAT),

    guest_first_name: form.data.firstName,
    guest_middle_name: form.data.middleName,
    guest_last_name: form.data.lastName,
    guest_title: form.data.title,
    guest_phone: phone,

    dob: moment.utc(dob).format(ISO_DATE_FORMAT),
    postcode: form.data.postcode,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)) ?? [],
  }
}

function formatTourExperienceItem(experienceItem: App.Checkout.TourV2ExperienceItem) {
  const count = sumUpOccupancies(experienceItem.occupants)

  return {
    id: experienceItem.purchasableOption.fkExperienceId,
    itinerary_id: experienceItem.purchasableOption.fkItineraryId,
    day_number: experienceItem.purchasableOption.day,
    duration: experienceItem.purchasableOption.duration,
    name: experienceItem.purchasableOption.name,
    description: experienceItem.purchasableOption.description,
    location: experienceItem.purchasableOption.location,
    price: experienceItem.purchasableOption.price,
    time_slot: experienceItem.purchasableOption.timeSlot,
    count,
    date: experienceItem.date,
    transaction_key: experienceItem.transactionKey,
  }
}

function formatTourV2ItemGroup(
  /** An array of items for a particular tour/option/departure */
  items: Array<App.Checkout.TourV2Item>,
  experienceItems: Array<App.Checkout.TourV2ExperienceItem>,
  specialRequests: { [itemId: string]: string },
  travellerForms: Array<App.Checkout.TravellerForm>,
  primary: App.Checkout.TravellerForm,
  offer: App.Tours.TourV2Offer,
  schema: App.Checkout.FormSchema,
  checkoutWithMemberPrice: boolean,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const occupants = schema.properties
  const occupantsRoomMap: Record<string, { index: number, unit: string }> = Object.entries(occupants)
    .reduce((acc, [key, value]) => {
      const relatedItem = value.relatedItem
      if (!relatedItem) { return acc }
      return { ...acc, [key]: { index: relatedItem.index, type: relatedItem.unit } }
    }, {})
  const travellerDetails = travellerForms.map((form, i) => {
    const formId = form.id
    const roomType = items[occupantsRoomMap[formId].index]?.purchasableOption.roomType
    const roomNumber = occupantsRoomMap[formId].index + 1
    const res = {
      first_name: form.data.firstName,
      middle_name: form.data.middleName ? form.data.middleName : undefined,
      last_name: form.data.lastName,
      email_address: form.data.email,
      title: form.data.title,
      gender: deduceGenderFromTitle(form.data.title as App.Title),
      type: form.id.split('-')[0],
      date_of_birth: form.data.dateOfBirth,
      is_lead_passenger: i === 0,
      room_type: roomType,
      room_number: roomNumber,
      phone_prefix: form.data.phonePrefix,
      phone: form.data.phone,
      departure_airport: form.data.departureAirportsList ?? undefined,
    }

    return res
  })

  const phone = `+${primary.data.phonePrefix}${primary.data.phone}`

  // non-TTC tours don't need address
  const requiresAddress = offer.productType === 'connection_tour'

  const address = {
    city: primary.data.city,
    country_code: primary.data.countryOfResidence,
    line1: primary.data.address,
    postcode: primary.data.auPostcodeField ?? primary.data.usPostcodeField ?? primary.data.postcode,
    region: primary.data.auStateField ?? primary.data.usStateField ?? primary.data.state,
  }

  const contactDetails = {
    phone,
    email: primary.data.email,
    ...(requiresAddress ? { address } : {}),
  }

  const price = sum(
    items.map(
      item => generateDepartureRoomPriceBreakdown(
        item.occupancy,
        item.purchasableOption,
        offer,
      ).totalPrice,
    ),
  )

  const memberPrice = sum(
    items.map(
      item => generateDepartureRoomPriceBreakdown(
        item.occupancy,
        item.purchasableOption,
        offer,
      ).totalMemberPrice,
    ),
  )

  const tourPrice = checkoutWithMemberPrice && memberPrice > 0 ? memberPrice : price
  const totalPrice = sum([tourPrice, ...experienceItems.map(item => item.total!)])

  const tourOptionId = offer.variations?.[items[0].purchasableOption.fkVariationId]?.fkTourOptionId
  const package_option = getPackageOptionType(items[0].purchasableOption.roomType)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'tourCruiseAndOptionalExperiences' } })
  return {
    type: 'tour',
    product_type: offer.productType,
    // Since the items are grouped by departure, these IDs are the same for the entire group. Can take them from the first item
    tour_id: items[0].offerId,
    fine_print: offer.finePrint?.sections,
    tour_option_id: tourOptionId,
    departure_id: items[0].purchasableOption.fkDepartureId,
    transaction_key: items[0].transactionKey,
    price: totalPrice,
    rooms: items.map(item => ({
      room_type: item.purchasableOption.roomType,
      adult_count: item.occupancy.adults,
      child_count: item.occupancy.childrenAge?.length ?? 0,
      child_ages: item.occupancy.childrenAge ?? [],
      special_requests: specialRequests[item.itemId],
      room_type_pricing_id: item.purchasableOption.fkRoomTypePricingId,
    })),
    traveller_details: travellerDetails,
    contact_details: contactDetails,
    package_option,
    optional_experiences: experienceItems.map(formatTourExperienceItem),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, items[0].transactionKey)),
  }
}

function formatTourV2ItemGroupDateChanges(
  items: Array<App.Checkout.TourV2Item>,
  offer: App.Tours.TourV2Offer,
  checkoutWithMemberPrice: boolean,
) {
  const itemsPricing = items.map(
    item => generateDepartureRoomPriceBreakdown(
      item.occupancy,
      item.purchasableOption,
      offer,
    ),
  )
  const price = sum(itemsPricing, item => item.totalPrice)
  const memberPrice = sum(itemsPricing, item => item.totalMemberPrice)
  const tourPrice = checkoutWithMemberPrice && memberPrice > 0 ? memberPrice : price

  const originalPrice = items[0].originalTotal ?? 0
  const diffPrice = tourPrice - originalPrice

  const package_option = getPackageOptionType(items[0].purchasableOption.roomType)

  return {
    departure_id: items[0].purchasableOption.fkDepartureId,
    transaction_key: items[0].transactionKey,
    price: tourPrice,
    difference_to_pay: diffPrice > 0 ? diffPrice : 0,
    package_option,
    rooms: items.map(item => ({
      room_type: item.purchasableOption.roomType,
      adult_count: item.occupancy.adults,
      child_count: item.occupancy.childrenAge?.length ?? 0,
      child_ages: item.occupancy.childrenAge ?? [],
      room_type_pricing_id: item.purchasableOption.fkRoomTypePricingId,
    })),
  }
}

function formatCarHireItem(
  item: App.Checkout.CarHireItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  arrivalFlightNumber: string | null,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const primaryDriver = travellerForms.find((form) => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const offer = item.offer

  const selectedRateOption = getCarHireSelectedRateOption(item)

  const totalPrice = selectedRateOption.payNowAmount + (item.selectedInsurance?.total || 0)
  const payOnPickUpAmount = selectedRateOption.payOnArrivalAmount + sum(item.selectedAddons, addon => addon.total ?? 0)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'car_hire' } })

  return {
    transaction_key: item.transactionKey,
    type: 'car_hire',
    offer,
    pickUpLocation: {
      code: offer.pickUp.id,
      date: moment.utc(`${item.pickUpDate}T${item.pickUpTime}`),
    },
    returnLocation: {
      code: offer.dropOff.id,
      date: moment.utc(`${item.dropOffDate}T${item.dropOffTime}`),
    },
    reference: {
      dateTime: selectedRateOption.dateTime,
      id: selectedRateOption.id,
      idContext: selectedRateOption.idContext,
      type: selectedRateOption.type,
      url: selectedRateOption.url,
    },
    vehicle: {
      code: offer.vehicle.code,
      model: offer.vehicle.model,
      image: offer.vehicle.image.url,
      transmissionType: offer.vehicle.transmission,
      baggageQuantity: offer.vehicle.luggage,
      passengerQuantity: offer.vehicle.seats,
      fuelType: offer.vehicle.fuelType,
      doorCount: offer.vehicle.doors,
      category: offer.vehicle.type,
      driveType: offer.vehicle.driveType,
      size: {
        code: offer.vehicle.sizeCode,
        description: offer.vehicle.type,
      },
      type: {
        code: offer.vehicle.typeCode,
        description: offer.vehicle.type,
      },
    },
    driver: {
      givenName: primaryDriver?.data.firstName,
      surname: primaryDriver?.data.lastName,
      email: primaryDriver?.data.email,
      phoneNumber: primaryDriver?.data.phone,
      phoneCode: primaryDriver?.data.phonePrefix,
      age: primaryDriver?.data.dateOfBirth ? getAge(primaryDriver.data.dateOfBirth, new Date(item.pickUpDate)) : undefined,
    },
    value: item.value,
    total: totalPrice,
    payOnPickUpAmount,
    originalPrice: selectedRateOption.originalPrice,
    qtyPassengers: 1, // TODO car hire remove mock
    availabilityUuid: offer.availabilityUuid,
    ...(arrivalFlightNumber && {
      arrivalDetails: {
        number: arrivalFlightNumber,
        transportationCode: TRANSPORTATION_CODE.AIRPLANE,
      },
    }),
    ...(item.selectedAddons && {
      specialEquipments: Object.values(item.selectedAddons),
    }),
    ...(item.selectedInsurance?.type === 'enhanced' && {
      insuranceReference: {
        id: item.selectedInsurance.id,
        amount: item.selectedInsurance.total,
        currencyCode: item.selectedInsurance.currency,
        url: item.selectedInsurance.url,
      },
    }),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(item.bundledOrderId && {
      bundledOrderId: item.bundledOrderId,
    }),
  }
}

function formatCruiseItem(
  itemView: App.Checkout.CruiseAccommodationItemView,
  travellerForms: Array<App.Checkout.TravellerForm>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  shouldEarnLoyaltyPoints: boolean,
  personTitles: Array<App.Cruises.BookingPersonTitles>,
  shouldUseCruiseMemberPrice: boolean,
) {
  const item = itemView.item
  const titleGenderMap = buildBookingTitleGenderMap(personTitles)
  const travellerDetails = travellerForms.map((form, i) => ({
    first_name: form.data.firstName,
    last_name: form.data.lastName,
    email: form.data.email,
    title: form.data.title,
    gender: form.data.title ? titleGenderMap[form.data.title] : undefined,
    type: form.id.split('-')[0],
    date_of_birth: form.data.dateOfBirth,
    is_lead_passenger: i === 0,
  }))

  const isMemberPriceAvailable = shouldUseCruiseMemberPrice && itemView.totals.memberPrice > 0

  /* These fields are used on svc-order to calculate the potential savings
   * https://github.com/lux-group/svc-order/blob/master/src/models/svc-membership/index.ts#L670
   */
  const publicPrice = isMemberPriceAvailable ? itemView.totals.price : undefined
  const memberPrice = isMemberPriceAvailable ? itemView.totals.memberPrice : undefined

  const total = isMemberPriceAvailable ? memberPrice : itemView.totals.price

  return {
    type: 'cruise',
    departure_id: item.departureId,
    departure_date: item.startDate,
    arrival_date: item.endDate,
    session_id: item.sessionId,
    booking_id: item.bookingId,
    transaction_key: item.transactionKey,
    total,
    public_price: publicPrice,
    member_price: memberPrice,
    traveller_details: travellerDetails,
    offer_name: item.offerName,
    offer_id: item.offerId,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    should_earn_lux_loyalty_points: shouldEarnLoyaltyPoints,
  }
}

function getTravellerBillingAddress(
  primaryForm: App.Checkout.TravellerForm | undefined,
): { postalCode?: string; stateProvinceCode?: string } {
  return {
    postalCode: primaryForm?.data.auPostcodeField || primaryForm?.data.usPostcodeField || primaryForm?.data.postcode,
    stateProvinceCode: primaryForm?.data.auStateField || primaryForm?.data.usStateField || primaryForm?.data.state,
  }
}

/**
 * One occupancy, one order item. Price is not sent — svc-order re-reads the
 * enquiry session and charges `session.data.pricing.sellTotal`.
 */
export function formatAccommodationV2Item(
  item: App.Checkout.AccommodationV2Item,
  primaryForm: App.Checkout.TravellerForm | undefined,
  specialRequest: string,
  roomGuestItems: Array<RoomGuestItem>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  /** Set only when the selected rate is pay at property; the booking then carries billing details. */
  billingCountryCode: string | undefined,
  session?: App.AccommodationEnquirySession,
) {
  const roomGuestIndex = roomGuestItems.findIndex(roomGuestItem => roomGuestItem.itemId === item.itemId)
  const guestNames = getRoomGuestNames({
    itemId: item.itemId,
    primaryForm,
    fallbackFirstName: primaryForm?.data.firstName,
    fallbackLastName: primaryForm?.data.lastName,
    isEnabled: roomGuestIndex >= 0,
    isPrimaryRoom: roomGuestIndex === 0,
  })

  const bedGroupId = item.bedGroupId ?? session?.data.supplierMetaData.selectedRate.bedGroupId
  const { postalCode, stateProvinceCode } = getTravellerBillingAddress(primaryForm)

  return {
    brand: config.BRAND,
    type: ITEM_TYPE_ACCOMMODATION_V2,
    transaction_key: item.transactionKey,
    session_id: item.sessionId,
    // Needed so assignBundleGroupIds can match a bundled experience's parent_cart_item_ids.
    cart_item_id: item.itemId,
    booking_email: primaryForm?.data.email,
    booking_phone: primaryForm?.data.phonePrefix && primaryForm?.data.phone
      ? `+${primaryForm.data.phonePrefix}${primaryForm.data.phone}`
      : undefined,
    guest_first_name: guestNames.firstName,
    guest_last_name: guestNames.lastName,
    ...(specialRequest ? { guest_special_requests: specialRequest } : {}),
    ...(bedGroupId ? { bed_group_id: bedGroupId } : {}),
    ...(billingCountryCode && {
      billing_address: {
        country_code: billingCountryCode,
        ...(postalCode ? { postal_code: postalCode } : {}),
        ...(stateProvinceCode ? { state_province_code: stateProvinceCode } : {}),
      },
    }),
    ...(item.accommAvailabilitySessionId ? { accomm_availability_session_id: item.accommAvailabilitySessionId } : {}),
    ...(item.accommCampaignId ? { accomm_campaign_id: item.accommCampaignId } : {}),
    ...(!!item.accommBundleIds?.length && {
      accomm_bundle_ids: item.accommBundleIds,
      bundle_optin: item.bundleOptin ?? false,
    }),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
  }
}

function formatBedbankItem(
  itemId: string,
  item: App.Checkout.BedbankHotelItem,
  bedbankItems: Array<App.Checkout.BedbankHotelItem>,
  specialRequests: string,
  travellerForms: Array<App.Checkout.TravellerForm>,
  formSchema: App.Checkout.FormSchema | undefined,
  roomGuestItems: Array<RoomGuestItem>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  cartHasPropertyCollectItem: boolean,
  regionCode: string,
  offer: App.BedbankOffer,
  markupSource?: ChannelMarkupSource,
  existingOrderTraveller?: App.ExistingOrderBedbankTraveller,
  sessions?: Record<string, {
    data: App.BedbankEnquiry | null
    state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
  }>,
  checkoutWithMemberPrice = false,
  defaultFirstRoomToPrimary = true,
  uniquePrimaryRoomGuest = false,
) {
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const { postalCode, stateProvinceCode } = getTravellerBillingAddress(primaryForm)
  let travellerFormData = {
    bookingPhone: primaryForm?.data.phonePrefix && primaryForm?.data.phone && `+${primaryForm.data.phonePrefix}${primaryForm.data.phone}`,
    bookingEmail: primaryForm?.data.email,
    guestFirstName: primaryForm?.data.firstName,
    guestLastName: primaryForm?.data.lastName,
    postalCode,
    stateProvinceCode,
  }

  if (existingOrderTraveller) {
    travellerFormData = {
      ...travellerFormData,
      ...existingOrderTraveller,
    }
  }

  // Channel markup (LPP): persist the markup that priced this item so svc-order re-applies it.
  // Pricing mirrors bedbankRateMap so price-gated v2 campaigns resolve as on the offer page.
  const sessionTotals = item.sessionId ? sessions?.[item.sessionId]?.data?.totals : undefined
  const channelMarkupPricing = sessionTotals
    ? { total: sessionTotals.exclusive + sessionTotals.taxesAndFees, luxPlusPrice: sessionTotals.memberInclusive || undefined }
    : undefined
  // svc-order charges the member total when the customer checks out with member pricing.
  const chargedMemberPrice = checkoutWithMemberPrice && !!sessionTotals?.memberInclusive
  const channelMarkupIdentity = resolveChannelMarkupOrderIdentity(offer.id, offer.type, markupSource, channelMarkupPricing, { member: chargedMemberPrice })

  const formattedItem = {
    brand: config.BRAND,
    type: ITEM_TYPE_BEDBANK,
    cart_item_id: itemId,
    session_id: item.sessionId,
    bed_group_id: item.bedGroupId,
    property_id: item.offerId,
    room_type_id: item.roomId,
    room_rate_id: item.roomRateId,
    is_flight_bundle: item.isFlightBundle,
    transaction_key: item.transactionKey,
    booking_email: travellerFormData?.bookingEmail,
    booking_phone: travellerFormData.bookingPhone,
    rooms: (() => {
      const formattedRooms = bedbankItems.map((roomItem) => {
        const roomGuestIndex = roomGuestItems.findIndex(roomGuestItem => roomGuestItem.itemId === roomItem.itemId)
        const guestNames = getRoomGuestNames({
          itemId: roomItem.itemId,
          primaryForm,
          travellerForms,
          formSchema,
          fallbackFirstName: travellerFormData.guestFirstName,
          fallbackLastName: travellerFormData.guestLastName,
          isEnabled: roomGuestIndex >= 0,
          isPrimaryRoom: roomGuestIndex === 0 && defaultFirstRoomToPrimary,
        })

        return { roomItem, roomGuestIndex, guestNames }
      })
      const roomGuestItemIds = formattedRooms
        .filter(room => room.roomGuestIndex >= 0)
        .map(room => room.roomItem.itemId)
      const namesByItemId = arrayToMap(
        formattedRooms.filter(room => room.roomGuestIndex >= 0),
        room => room.roomItem.itemId,
        room => room.guestNames,
      )
      const primaryRoomGuestItemId = uniquePrimaryRoomGuest
        ? getPrimaryRoomGuestItemId(roomGuestItemIds, namesByItemId, primaryForm)
        : undefined

      return formattedRooms.map(({ roomItem, roomGuestIndex, guestNames }) => ({
        guest_first_name: guestNames.firstName,
        guest_last_name: guestNames.lastName,
        ...(uniquePrimaryRoomGuest && roomGuestIndex >= 0 && {
          is_primary: roomItem.itemId === primaryRoomGuestItemId,
        }),
        ...(!uniquePrimaryRoomGuest && config.agentHub.isEnabled && {
          is_primary: isSameRoomGuestName(
            guestNames.firstName,
            guestNames.lastName,
            primaryForm?.data.firstName,
            primaryForm?.data.lastName,
          ),
        }),
        guest_special_requests: specialRequests,
        occupancy: generateOccupancyStringByRoom(roomItem.occupancy),
      }))
    })(),
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    is_upgraded_rate: item.isUpgradedRate,
    accomm_availability_session_id: item.accommAvailabilitySessionId,
    accomm_campaign_id: item.accommCampaignId,
    accomm_bundle_ids: item.accommBundleIds,
    // Post-purchase replacements carry the original booking's group so the new item
    // joins its existing bundled children; first-purchase items leave this unset and
    // svc-order stamps the group itself.
    ...(item.bundleGroupId && { bundle_group_id: item.bundleGroupId }),
    bundle_optin: item.bundleOptin ?? false,
    loyalty_upgrade_source: formatLoyaltyUpgradeSource(item.loyaltyPropertyRateUpgrade),
    ...(channelMarkupIdentity
      ? {
          channel_markup_id: channelMarkupIdentity.channelMarkupId,
          channel_markup_round_to_ten: true,
          // The specific markup within the campaign, so svc-order re-applies the exact one it charged.
          // svc-order names this field `channel_markup_discount_id`; it carries the client's `markupId`.
          ...(channelMarkupIdentity.version === 'v2' ? { channel_markup_discount_id: channelMarkupIdentity.markupId } : {}),
        }
      : {}),
  }

  if (cartHasPropertyCollectItem && item.roomRateId && item.sessionId && sessions?.[item.sessionId]?.data?.paymentMethod === 'propertyCollect') {
    return {
      ...formattedItem,
      billing_address: {
        country_code: regionCode,
        postal_code: travellerFormData.postalCode,
        state_province_code: travellerFormData.stateProvinceCode,
      },
    }
  }

  return formattedItem
}

function formatPassengers(
  travellerForms: Array<App.Checkout.TravellerForm>,
  flightItem: App.Checkout.FlightItem,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0 || !flightItem?.flights[0]) { return [] }
  const carrierCodes = flightItem.flights.map((f) => f.carrier)
  const paxList = flightItem.passengers

  const paxBaggageMap = flightItem.flights.map(flight => flight.extras.baggage)
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = paxList.map((pax: App.Checkout.FlightPassenger) => {
    const paxFormData = travellerForms.find(form => pax.id === form.id)
    if (!paxFormData) { return null }
    const paxSchema = schemaData?.accommodationFlightSchema.properties[pax.id]

    const allBaggage = paxBaggageMap.map(baggage => baggage[pax.id])
    const baggage = buildBaggagePayload(allBaggage[0], allBaggage[1])
    return buildPassengerPayload(pax, paxFormData, primaryForm, baggage, carrierCodes, paxSchema)
  }).filter(excludeNullOrUndefined)

  return result
}

function formatFlightItem(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isFlightChangeDates: boolean,
  isLuxPlusPriced: boolean,
): Array<unknown> {
  const isReturn = journeys.length === 2
  const isSameProvider = isReturn && new Set(journeys.map(journey => journey.provider)).size === 1

  if (isFlightChangeDates) {
    return journeys
  }

  if (item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN || item.viewType === FlightViewTypes.MULTI_CITY || isSameProvider) {
    return formatReturnFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData, isLuxPlusPriced)
  }

  return formatFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData, isLuxPlusPriced)
}

function getFlightOrderPricing(
  item: App.Checkout.FlightItem,
  flight: App.Checkout.FlightItemFlight,
  index: number,
  journey: App.JourneyV2,
  isLuxPlusPriced: boolean,
): { cost: number, tax: number } {
  const seatEligiblePassengers = item.passengers.filter(passenger => passenger.type !== 'infant').length
  const selectedPrice = getEffectiveFlightPrice(journey, seatEligiblePassengers)
  const useRegularPrice = !isLuxPlusPriced && !item.isFnFBooking && !item.isClassicReward

  if (useRegularPrice) {
    const fareFamilyRegularCost = flight.fareFamily?.regularCost
    if (fareFamilyRegularCost) {
      let cost = fareFamilyRegularCost.all
      if (item.bundleBudget) {
        cost = index === 0 ?
            getBudgetedSelectedDepartingLegPrice(item, cost) :
            applyFeederBudgetToCost(cost, item.bundleBudget, 'returning')
      }
      return {
        cost,
        tax: fareFamilyRegularCost.totalTax ?? journey.regularPrice?.all.totalTax ?? selectedPrice.all.totalTax,
      }
    }
    const regularCost = journey.regularPrice?.all.totalFare
    if (regularCost !== undefined) {
      return {
        cost: regularCost,
        tax: journey.regularPrice?.all.totalTax ?? selectedPrice.all.totalTax,
      }
    }
  }

  return {
    cost: flight.cost ?? selectedPrice.all.totalFare,
    tax: selectedPrice.all.totalTax,
  }
}

function formatFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isLuxPlusPriced: boolean,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = item.flights.map((flight, index) => {
    const segmentJourney = journeys[index]

    if (segmentJourney) {
      const flightPricing = getFlightOrderPricing(item, flight, index, segmentJourney, isLuxPlusPriced)
      const baggageOptions = baggageOptionsMap(segmentJourney.flightGroup.flights[0])?.baggage ?? []

      const cost = sum([
        flightPricing.cost,
        sum(Object.values(item?.otherFees ?? [])),
        computeBaggageCostForPaxList(item.passengers, baggageOptions, flight.extras.baggage),
      ])

      const tax = flightPricing.tax

      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

      return {
        provider: segmentJourney.provider,
        transaction_key: item.transactionKey,
        type: CHECKOUT_ITEM_TYPE_FLIGHT,
        isFnFBooking: item.isFnFBooking,
        offer_type: [(item.bundledItemIds?.length ?? 0) > 0 ? 'hotel' : null],
        contact_email_address: primaryTraveller?.data.email,
        contact_phone_number: primaryTraveller ? `${primaryTraveller?.data.phonePrefix}${primaryTraveller?.data.phone}` : undefined,
        passengers: formatPassengers(travellerForms, item, schemaData),
        journey: {
          id: flight.journeyId,
          cost,
          tax,
          searchId: item.searchId,
        },
        viewType: item.viewType,
        item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
        ...(flight.paymentScheduleSupported && { is_payment_schedule_supported: flight.paymentScheduleSupported }),
        is_lux_plus_priced: isLuxPlusPriced,
      }
    }
  }).filter(excludeNullOrUndefined)

  return result
}

function formatReturnFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
  isLuxPlusPriced: boolean,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const flightPricing = item.flights.map((flight, index) => getFlightOrderPricing(item, flight, index, journeys[index], isLuxPlusPriced))

  const cost = sum([
    ...flightPricing.map(price => price.cost),
    sum(Object.values(item?.otherFees ?? [])),
    ...journeys.map((journey, index) => {
      const baggageOptions = baggageOptionsMap(journey.flightGroup.flights[0])?.baggage ?? []
      return computeBaggageCostForPaxList(item.passengers, baggageOptions, item.flights[index]?.extras.baggage ?? {})
    }),
  ])

  const tax = sum(flightPricing, price => price.tax)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

  // Check if any flight has paymentScheduleSupported for return flights
  const isPaymentScheduleSupported = item.flights.some(flight => flight.paymentScheduleSupported === true)

  const result = {
    provider: journeys[0].provider,
    transaction_key: item.transactionKey,
    type: CHECKOUT_ITEM_TYPE_FLIGHT,
    isFnFBooking: item.isFnFBooking,
    offer_type: [(item.bundledItemIds?.length ?? 0) ? 'hotel' : null],
    contact_email_address: primaryTraveller?.data.email,
    contact_phone_number: primaryTraveller ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined,
    passengers: formatPassengers(travellerForms, item, schemaData),
    journey: {
      id: journeys[0].id,
      cost,
      tax,
      searchId: item.searchId,
      selectedFareIds: item.flights.map(flight => flight.journeyId),
    },
    viewType: item.viewType === FlightViewTypes.MULTI_CITY ?
      FlightViewTypes.MULTI_CITY :
      FlightViewTypes.TWO_ONE_WAYS_AND_RETURN,
    item_discounts: itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
    ...(isPaymentScheduleSupported && { is_payment_schedule_supported: true }),
    is_lux_plus_priced: isLuxPlusPriced,
  }

  return [result]
}

const getFormattedLeInstantBookingHotelItems = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.calendar.calendarsByOccupancyLereGroups,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesLereGroups,
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getAccommodationAndExperienceItemDiscounts(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getChannelMarkupSource(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (state: App.State) => state.offer.propertyOfferMappings,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getLereDynamicPricingGroup(state),
  (state: App.State) => isOverlappingRoomGuestNamesEnabled(state),
  (state: App.State) => isOverlappingFlashRoomGuestNamesEnabled(state),
  (
    items,
    accommodationItems,
    offers,
    calendarsRatesByOccupancy,
    calendarsByOccupancyLereGroups,
    availableRatesByOccupancy,
    offerAvailableRatesLereGroups,
    travellerForms,
    specialRequests,
    account,
    cartMode,
    promoCodeItemDiscounts,
    checkoutWithMemberPrice,
    markupSource,
    connectedRoomOfferIds = [],
    propertyOfferMappings = {},
    bedbankOffers = {},
    schemaData,
    lereGroup,
    overlappingRoomGuestNamesEnabled,
    includeFlashOverlappingRoomGuests,
  ) => {
    const eligibleRoomGuestItems = getRoomGuestItems(accommodationItems, offers)
    const collectCustomerOverlappingRoomGuests = !config.agentHub.isEnabled && overlappingRoomGuestNamesEnabled
    const includeFlashRoomGuests = config.agentHub.isEnabled || includeFlashOverlappingRoomGuests
    const mappingRoomGuestItems = getMappedRoomGuestEligibleItems(
      eligibleRoomGuestItems,
      offers,
      overlappingRoomGuestNamesEnabled,
      includeFlashRoomGuests,
    )
    const roomGuestItems = getCheckoutRoomGuestItems(
      accommodationItems,
      offers,
      config.agentHub.isEnabled,
      bedbankOffers,
      propertyOfferMappings,
      collectCustomerOverlappingRoomGuests,
      includeFlashOverlappingRoomGuests,
    )
    const defaultFirstRoomToPrimary = config.agentHub.isEnabled ||
      shouldDefaultFirstCollectedRoomToPrimary(
        mappingRoomGuestItems,
        roomGuestItems,
        getRoomGuestPropertyIdByItemId(mappingRoomGuestItems, offers, bedbankOffers, propertyOfferMappings),
      )
    const formSchema = schemaData?.accommodationFlightSchema
    const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
    const primaryRoomGuestItemId = getPrimaryRoomGuestItemId(
      roomGuestItems.map(item => item.itemId),
      getResolvedRoomGuestNamesByItemId(roomGuestItems, travellerForms, formSchema, defaultFirstRoomToPrimary),
      primaryTraveller,
    )

    let hasRequiredData = true
    if (
      overlappingRoomGuestNamesEnabled &&
      isRoomGuestPropertyMappingPending(mappingRoomGuestItems, offers, bedbankOffers, propertyOfferMappings)
    ) {
      hasRequiredData = false
    }
    const data = items
      .filter(isInstantBookingLEHotelItem)
      .map((item) => {
        const offer = offers[item.offerId]
        if (!offer) {
          hasRequiredData = false
          return null
        }

        const offerLereGroup = getLereDynamicPricingGroupForOffer(offer, lereGroup)
        const {
          data: instantBookingPricing,
          hasRequiredData: hasRequiredInstantBookingPricing,
        } = getInstantBookingPriceAndSurcharge(item, offer, calendarsRatesByOccupancy, availableRatesByOccupancy, {
          calendarsByOccupancyLereGroups,
          offerAvailableRatesLereGroups,
          lereGroup: offerLereGroup,
        })

        const { itemDiscounts } = promoCodeItemDiscounts
        if (!hasRequiredInstantBookingPricing) {
          hasRequiredData = false
          return null
        }
        const specificItemDiscounts = filterItemDiscounts({ itemDiscounts, lookup: { specificType: 'accommodation' } })
        const roomGuestIndex = roomGuestItems.findIndex(roomGuestItem => roomGuestItem.itemId === item.itemId)
        const roomGuestItemsForOffer = roomGuestItems.filter(roomGuestItem =>
          roomGuestItem.offerId === item.offerId &&
          roomGuestItem.checkIn === item.checkIn &&
          roomGuestItem.checkOut === item.checkOut,
        )
        const connectedRoomContactNames = getRoomGuestContactNames({
          roomGuestItems: roomGuestItemsForOffer,
          primaryForm: primaryTraveller,
          travellerForms,
          formSchema,
          fallbackFirstName: primaryTraveller?.data.firstName,
          fallbackLastName: primaryTraveller?.data.lastName,
          defaultFirstRoomToPrimary,
        })

        return formatLeHotelItem(
          item,
          travellerForms,
          specialRequests[item.itemId],
          offer,
          instantBookingPricing,
          account,
          checkoutWithMemberPrice,
          connectedRoomOfferIds,
          cartMode,
          undefined,
          specificItemDiscounts,
          markupSource,
          roomGuestIndex >= 0 ? roomGuestIndex : undefined,
          connectedRoomContactNames,
          lereGroup,
          roomGuestIndex >= 0 && item.itemId === primaryRoomGuestItemId,
          defaultFirstRoomToPrimary,
          formSchema,
          overlappingRoomGuestNamesEnabled,
        )
      })

    return { hasRequiredData, data }
  },
)

export const getFormattedVillaItems = createSelector(
  (state: App.State) => getVillaViews(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.arrivalDetails,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getChannelMarkupSource(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (state: App.State) => getLereDynamicPricingGroup(state),
  (
    views,
    travellerForms,
    specialRequests,
    account,
    checkoutWithMemberPrice,
    arrivalDetails,
    promoCodeItemDiscounts,
    markupSource,
    connectedRoomOfferIds = [],
    lereGroup,
  ) => {
    return views.data.map(view => {
      const { itemDiscounts: allItemDiscounts } = promoCodeItemDiscounts
      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'accommodation', cartItemIds: [view.item.itemId] } })
      return formatLeHotelItem(
        view.item,
        travellerForms,
        specialRequests[view.item.itemId],
        view.offer,
        view.totals,
        account,
        checkoutWithMemberPrice,
        connectedRoomOfferIds,
        undefined,
        arrivalDetails[view.item.itemId],
        itemDiscounts,
        markupSource,
        undefined,
        undefined,
        lereGroup,
      )
    })
  },
)

export const getFormattedSubscriptionJoinFeeItems = createSelector(
  (state: App.State) => getSubscriptionJoinItems(state),
  (state: App.State) => getSubscriptionJoinDiscountItems(state),
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (state: App.State) => state.auth.account,
  (items, subJoinDiscounts, subscriptionItems, account): Array<FormattedOrderSubscriptionItem> => {
    // Get phone data from the main subscription item (should only be one for standalone checkout)
    const subscriptionItem = subscriptionItems[0]

    // We don't want to send the waived items to the backend
    return items.filter(item => !item.waived).map<FormattedOrderSubscriptionItem>(item => ({
      subscription_offer_id: item.subscriptionOfferId,
      type: 'subscription',
      transaction_key: item.transactionKey,
      total: item.amount,
      sub_type: 'joining_fee',
      item_discounts: subJoinDiscounts.itemDiscounts.map(d => formatItemDiscount(d, item.transactionKey)),
      customer_phone: subscriptionItem?.phone || account.phone,
      customer_phone_prefix: subscriptionItem?.phonePrefix || account.phonePrefix,
    }))
  },
)

export const getFormattedCartItems = createSelector(
  (state: App.State) => state.checkout.cart,
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.auth.account,
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (state: App.State) => getFormattedLeInstantBookingHotelItems(state),
  (state: App.State) => getFormattedTourV2Items(state),
  (state: App.State) => getBedbankItemIdToLeadItemId(state),
  (state: App.State) => getFormattedExperienceItems(state),
  (state: App.State) => getFormattedTransferItems(state),
  (state: App.State) => getFormattedInsuranceItems(state),
  (state: App.State) => getFormattedBookingProtectionItems(state),
  (state: App.State) => getFormattedCruiseItems(state),
  (state: App.State) => getFormattedBundleAndSaveItems(state),
  (state: App.State) => getFormattedGiftCardItems(state),
  (state: App.State) => getFormattedVillaItems(state),
  (state: App.State) => getFormattedLuxPlusSubscriptionItems(state),
  (state: App.State) => getFormattedSubscriptionJoinFeeItems(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getFormattedTourExperienceItems(state),
  (state: App.State) => getBedbankChangeDatesTravellers(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getFormattedLoyaltyLoungePassItems(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => getFormattedMerchandiseItems(state),
  (state: App.State) => getFormattedLoyaltyPurchasePointsItems(state),
  (state: App.State) => getFormattedMerchandiseShippingItems(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getChannelMarkupSource(state),
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.checkout.accommodationSessions,
  getIsLuxPlusPricedFlight,
  (state: App.State) => state.offer.propertyOfferMappings,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => isOverlappingRoomGuestNamesEnabled(state),
  (state: App.State) => isOverlappingFlashRoomGuestNamesEnabled(state),
  formatCartItems,
)

export const getIsCheckoutOrderPayloadReady = createSelector(
  (state: App.State) => getFormattedCartItems(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (formattedCartItems, isAncillaryPayment): boolean => isAncillaryPayment || formattedCartItems.hasRequiredData,
)

export const getOrderGift = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (travellers): Order.OrderGift | undefined => {
    if (travellers.length < 2) { return undefined }
    const giftRecipient = travellers.find(traveller => traveller.id === GIFT_RECIPIENT_FORM_ID)
    if (giftRecipient) {
      const giftGiver = travellers.find(traveller => traveller.id === PRIMARY_TRAVELLER_FORM_ID)
      const giftPayload: Order.OrderGift = {
        receiver_email: giftRecipient.data.email!,
        receiver_name: giftRecipient ? `${giftRecipient.data.firstName} ${giftRecipient.data.lastName}` : 'undefined',
        giver_name: giftGiver ? `${giftGiver.data.firstName} ${giftGiver.data.lastName}` : '',
        scheduled_date: generateScheduleDate(giftRecipient.data.scheduledDate!),
      }
      if (giftRecipient.data.personalMessage) {
        giftPayload.gift_message = giftRecipient.data.personalMessage
      }
      return giftPayload
    }
  },
)
